import { useState } from "react";
import { usePathname, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../hooks/useLocale";
import { useTheme } from "../../hooks/useTheme";
import { usePrivacyPreferences } from "../../hooks/usePrivacyPreferences";
import { useToast } from "../../hooks/useToast";
import { openMarketingLink } from "../../lib/marketing-links";
import { shouldSuppressConsentPrompt } from "../../lib/privacy-consent-prompt-routes";

export function PrivacyConsentSheet() {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const { theme } = useTheme();
  const toast = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const { isHydrated, preferences, setConsentChoices } =
    usePrivacyPreferences();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (
    !isHydrated ||
    preferences.analytics !== "unknown" ||
    shouldSuppressConsentPrompt(pathname)
  ) {
    return null;
  }

  // "Allow" and "Decline" answer both purposes at once. Anyone who wants them
  // split uses Manage, which opens the two toggles in Settings.
  const decide = async (decision: "granted" | "denied") => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await setConsentChoices(
        { analytics: decision, marketing: decision },
        "prompt",
      );
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonBase: React.CSSProperties = {
    minHeight: 44,
    borderRadius: 999,
    padding: "0 14px",
    font: "inherit",
    fontSize: 12,
    fontWeight: 600,
    cursor: isSubmitting ? "default" : "pointer",
    opacity: isSubmitting ? 0.55 : 1,
  };

  return (
    <section
      aria-label={t("settings.privacy.consentTitle")}
      style={{
        position: "fixed",
        insetInline: 12,
        bottom: "max(12px, env(safe-area-inset-bottom))",
        zIndex: 10000,
        maxWidth: 1040,
        marginInline: "auto",
        borderRadius: 16,
        border: `1px solid ${theme.borderSubtle}`,
        background: theme.background,
        boxShadow: "0 16px 48px rgba(15, 23, 42, 0.15)",
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 360px", minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              color: theme.text,
              fontSize: 15,
              lineHeight: "20px",
            }}
          >
            {t("settings.privacy.consentTitle")}
          </h2>
          <p
            style={{
              margin: "2px 0 0",
              color: theme.textSecondary,
              fontSize: 12,
              lineHeight: "17px",
            }}
          >
            {t("settings.privacy.consentBody")}{" "}
            <button
              type="button"
              onClick={() => void openMarketingLink("privacy", locale)}
              style={{
                padding: 0,
                border: 0,
                background: "transparent",
                color: theme.buttonPrimaryBg,
                font: "inherit",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: "pointer",
              }}
            >
              {t("settings.privacyPolicy")}
            </button>{" "}
            <button
              type="button"
              onClick={() => router.push("/settings/privacy")}
              style={{
                padding: 0,
                border: 0,
                background: "transparent",
                color: theme.buttonPrimaryBg,
                font: "inherit",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: "pointer",
              }}
            >
              {t("settings.privacy.manage")}
            </button>
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            flex: "0 1 240px",
            width: "100%",
            gap: 8,
          }}
        >
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => void decide("denied")}
            style={{
              ...buttonBase,
              border: `1px solid ${theme.borderSubtle}`,
              background: theme.background,
              color: theme.text,
            }}
          >
            {t("settings.privacy.continueWithoutAnalytics")}
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => void decide("granted")}
            style={{
              ...buttonBase,
              border: `1px solid ${theme.buttonPrimaryBg}`,
              background: theme.buttonPrimaryBg,
              color: theme.buttonPrimaryText,
            }}
          >
            {t("settings.privacy.allowAnalytics")}
          </button>
        </div>
      </div>
    </section>
  );
}
